import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { trackCTAClick, trackThoughtSelect } from '../utils/analytics';

const thoughts = [
  "I can't stop thinking about this",
  "I don't know what to do",
  "I feel stuck",
  "I'm not sure I'm in the right career",
  "I keep going in circles",
  "I need to figure this out",
  'Something else...',
];

export default function DecisionPage() {
  const navigate = useNavigate();
  const [selectedThoughts, setSelectedThoughts] = useState([]);

  const toggleThought = (thought) => {
    trackThoughtSelect(thought, '/decide');
    setSelectedThoughts((prev) =>
      prev.includes(thought) ? prev.filter((item) => item !== thought) : [...prev, thought]
    );
  };

  return (
    <>
      <Helmet>
        <title>Trying To Figure Something Out? | Andlegur</title>
        <meta
          name="description"
          content="If your mind feels stuck, Andlegur helps you make sense of it quickly. Private, free, and built for clarity."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/decide" />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#05091B] font-serif text-indigo-100">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="/web_images/Andlegur%20web%2010.jpg"
            alt=""
            className="h-full w-full object-cover opacity-18 mix-blend-screen"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/35 via-transparent to-[#05091B]" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/60" />
        </div>

        <div className="relative z-10 min-h-screen px-4 sm:px-8 py-8 sm:py-10 flex items-center justify-center">
          <section className="w-full max-w-5xl rounded-3xl border border-indigo-400/50 bg-[#05091B] p-5 sm:p-8 lg:p-10 backdrop-blur-md shadow-2xl">
            <div className="mb-5 sm:mb-6 text-center">
              <Link to="/" className="text-indigo-300 hover:text-white text-base sm:text-lg tracking-wide transition-colors">
                Andlegur
              </Link>
            </div>

            <div className="grid gap-6 lg:grid-cols-2 lg:gap-10 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white">
                  Trying to figure
                  <br />
                  something out?
                </h1>

                <p className="mt-4 sm:mt-5 text-indigo-100 text-base sm:text-lg lg:text-xl leading-relaxed max-w-xl mx-auto lg:mx-0">
                  You're not alone. This is where you start making sense of it.
                </p>

                <button
                  onClick={() => { trackCTAClick('start_figuring_it_out', '/decide'); navigate('/join'); }}
                  className="mt-6 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-base sm:text-lg font-semibold px-7 py-3.5 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-indigo-700/35"
                >
                  Start Figuring It Out {'->'}
                </button>

                <p className="mt-4 text-indigo-300 text-xs sm:text-sm">
                  Private • Free • Takes less than a minute
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2.5 sm:gap-3">
                {thoughts.map((thought, idx) => {
                  const isSelected = selectedThoughts.includes(thought);
                  return (
                    <button
                      key={thought}
                      onClick={() => toggleThought(thought)}
                      className={`relative rounded-xl border px-3.5 py-2.5 pr-10 text-sm sm:text-base leading-snug backdrop-blur-sm text-left transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-300/70 bg-[#1A4BA0]/55 text-white shadow-lg shadow-blue-900/35'
                          : idx % 2 === 0
                            ? 'border-violet-300/35 bg-[#29194A]/70 text-violet-100 hover:border-blue-300/60'
                            : 'border-indigo-300/30 bg-[#11214A]/70 text-indigo-100 hover:border-blue-300/60'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span
                        className={`absolute top-2 right-2 h-5 w-5 rounded border flex items-center justify-center text-[11px] font-bold ${
                          isSelected
                            ? 'bg-blue-500 border-blue-300 text-white'
                            : 'bg-white/90 border-blue-300/70 text-blue-500'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                      {thought}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="mt-5 text-blue-300 text-sm sm:text-base font-semibold text-center">
              Continue with {selectedThoughts.length} thought{selectedThoughts.length === 1 ? '' : 's'} and find direction.
            </p>

            <div className="md:hidden mt-6 relative overflow-hidden rounded-2xl border border-indigo-300/30">
              <img
                src="/web_images/Andlegur%20web%2028.webp"
                alt="A person looking for direction and clarity"
                className="h-36 w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-linear-to-t from-[#05091B]/90 via-[#05091B]/35 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-center text-white text-sm font-semibold tracking-wide">
                Find Your Directions. Find Clarity
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
